import { Stack } from "@mui/material";
import { useLocation, useParams } from "react-router";

import { GqlPermissions } from "~/__generated__";
import GaiaPageLayoutColumn from "~/modules/app/components/GaiaPageLayoutColumn";
import useHasTenantPermission from "~/modules/auth/hooks/useHasPermission";

import BackToAllVisitsButton from "../../components/BackToAllVisitsButton";
import { ProjectReviewVisitV2OverviewPanel } from "../../components/ProjectReviewVisitV2OverviewPanel";
import { useProjectReviewVisitV2FinaReportRoute_GetData } from "./api/useProjectReviewVisitV2FinalReportRoute_GetData";
import { useProjectReviewVisitV2FinalReportRoute_RegenerateSummary } from "./api/useProjectReviewVisitV2FinalReportRoute_RegenerateSummary";
import FinalReportFunctionsCard from "./components/FinalReportFunctionsCard";
import FinalReportObservationsCard from "./components/FinalReportObservationsCard";
import FinalReportSummaryCard from "./components/FinalReportSummaryCard";
import FinalReportVisitCard from "./components/FinalReportVisitCard";
import FinalReportWeatherCard from "./components/FinalReportWeatherCard";
import { ProjectReviewVisitV2FinalReportRouteSkeleton } from "./components/ProjectReviewVisitV2FinalReportRouteSkeleton";
import { useSummaryBeingGenerated } from "./hooks/useSummaryBeingGenerated";

export default function ProjectReviewVisitV2FinalReportRoute() {
    const { clientSlug, projectSlug, visitId } = useParams();
    if (!clientSlug || !projectSlug || !visitId) throw new Error("invalid params");

    const location = useLocation();

    const {
        visit,
        panelStatus,
        isEverythingReady,
        weatherReport,
        ecologicalFunctions,
        observations,
        preciseLocationForm,
        loading,
        startPolling,
        stopPolling,
    } = useProjectReviewVisitV2FinaReportRoute_GetData(visitId);

    const isAdmin = useHasTenantPermission(GqlPermissions.Administrator);
    const regenerate = useProjectReviewVisitV2FinalReportRoute_RegenerateSummary();
    const summaryBeingGenerated = useSummaryBeingGenerated({
        status: visit?.status,
        approvedAt: visit?.approvedAt,
        summaryText: visit?.summaryText,
        startPolling,
        stopPolling,
    });

    const backToAllVisitsButtonHref = location.pathname.includes("/finalReport")
        ? "/visits?plannerVisitStatus=awaitingApproval"
        : `/c/${visit?.plannerArea.project.client.slug}/p/${visit?.plannerArea.project.slug}/planned`;

    if (loading) return <ProjectReviewVisitV2FinalReportRouteSkeleton />;

    return (
        <GaiaPageLayoutColumn maxWidth="xl">
            {visit && (
                <Stack direction="row" columnGap={2}>
                    <Stack rowGap={2} sx={{ flex: 2 }}>
                        <FinalReportVisitCard visit={visit} />
                        <FinalReportSummaryCard
                            visit={visit}
                            onRegenerate={isAdmin ? () => regenerate.regenerateSummary(visit.id) : undefined}
                            regenerating={regenerate.loading}
                            notUpdated={regenerate.written === false}
                            error={regenerate.error}
                            beingGenerated={summaryBeingGenerated}
                        />
                        {weatherReport && <FinalReportWeatherCard weatherReport={weatherReport} />}
                        {ecologicalFunctions && (
                            <FinalReportFunctionsCard
                                project={visit.plannerArea.project}
                                ecologicalFunctions={ecologicalFunctions}
                            />
                        )}
                        {observations?.isObservationsSectionAllowed && preciseLocationForm && (
                            <FinalReportObservationsCard
                                observations={observations.data}
                                preciseLocationForm={preciseLocationForm}
                                project={visit.plannerArea.project}
                            />
                        )}
                    </Stack>
                    {isAdmin && (
                        <Stack position="sticky" top={0} alignSelf="flex-start" flex={1} rowGap={2}>
                            <ProjectReviewVisitV2OverviewPanel
                                visit={visit}
                                loading={loading}
                                panelStatus={panelStatus}
                                isEverythingReady={isEverythingReady}
                                showFinalReportButton={false}
                            />
                            <BackToAllVisitsButton to={backToAllVisitsButtonHref} />
                        </Stack>
                    )}
                </Stack>
            )}
        </GaiaPageLayoutColumn>
    );
}
